import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonService } from '../../common-service';
import { environment } from '../../../../../environments/environment';
import { ReturnStruct } from '../../structs/return.struct';
import { EmpresaResponse } from '../../responses/usuario/empresa.response';
import { CasasDecimaisRequest } from '../../requests/usuario/casas-decimais.request';
import { CasasDecimaisResponse } from '../../responses/usuario/casas-decimais.response';
import { EmpresaResulthLocalConnectRequest } from '../../requests/usuario/empresa-resulth-local-connect.request';
import { ResulthLocalConnectResponse } from '../../responses/usuario/resulth-local-connect.response';
import { EmpresaLogoRequest } from '../../requests/usuario/empresa-logo.request';
import { EmpresaDadosFiscaisRequest } from '../../requests/usuario/empresa-dados-fiscais.request';
import { EmpresaRegimeTributarioRequest } from '../../requests/usuario/empresa-regime-tributario.request';
import { EmpresaRegimeTributarioResponse } from '../../responses/usuario/empresa-regime-tributario.response';
import { EmpresaMultiempresaRequest } from '../../requests/usuario/empresa-multiempresa.request';
import { EmpresaMultiempresaResponse } from '../../responses/usuario/empresa-multiempresa.response';
import { ListaEmpresaMultiempresaResponse } from '../../responses/usuario/lista-empresa-multiempresa.response';

@Injectable({
  providedIn: 'root',
})
export class EmpresaService extends CommonService {
  constructor(private httpClient: HttpClient) {
    super();
  }

  public obterPorId(idEmpresa: number): Observable<EmpresaResponse> {
    return this.httpClient
      .get<EmpresaResponse>(
        environment.urlApiUsuario + `Empresa/idEmpresa/${idEmpresa}`,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  public obterDadosPorId(idEmpresa: number): Observable<EmpresaResponse> {
    return this.httpClient
      .get<EmpresaResponse>(
        environment.urlApiUsuario + `GetEmpresa/idEmpresa/${idEmpresa}`,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  public obterCasasDecimaisPorId(idEmpresa: number): Observable<CasasDecimaisResponse> {
    return this.httpClient
      .get<CasasDecimaisResponse>(
        environment.urlApiUsuario + `Empresa/casasDecimais/idEmpresa/${idEmpresa}`,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  public obterResulthLocalConnectPorId(idEmpresa: number): Observable<ResulthLocalConnectResponse> {
    return this.httpClient
      .get<ResulthLocalConnectResponse>(
        environment.urlApiUsuario + `Empresa/resulthLocalConnect/idEmpresa/${idEmpresa}`,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  public alterarCasasDecimais(
    idEmpresa: number,
    casasDecimaisRequest: CasasDecimaisRequest
  ): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(
        environment.urlApiUsuario + `Empresa/casasDecimais/idEmpresa/${idEmpresa}`,
        casasDecimaisRequest,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  public alterarResulthLocalConnect(
    idEmpresa: number,
    empresaResulthLocalConnectRequest: EmpresaResulthLocalConnectRequest
  ): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(
        environment.urlApiUsuario + `Empresa/resulthLocalConnect/idEmpresa/${idEmpresa}`,
        empresaResulthLocalConnectRequest,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  /** Campos editáveis da tela Informações da Empresa. A logo continua em `salvarLogo`. */
  public alterarDadosFiscais(
    idEmpresa: number,
    empresaDadosFiscaisRequest: EmpresaDadosFiscaisRequest
  ): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(
        environment.urlApiUsuario + `Empresa/dadosFiscais/idEmpresa/${idEmpresa}`,
        empresaDadosFiscaisRequest,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Recorte para quem está fora da tela de Empresa — hoje a Natureza de Operação, que consulta
   * isso antes de perguntar o regime ao usuário. A empresa vem do token, por isso não há id.
   */
  public obterRegimeTributario(): Observable<EmpresaRegimeTributarioResponse> {
    return this.httpClient
      .get<EmpresaRegimeTributarioResponse>(
        environment.urlApiUsuario + 'Empresa/regimeTributario',
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Grava só o regime tributário e o regime de apuração, sem tocar no resto do bloco fiscal —
   * diferente de `alterarDadosFiscais`, que substitui o bloco inteiro.
   */
  public salvarRegimeTributario(
    empresaRegimeTributarioRequest: EmpresaRegimeTributarioRequest
  ): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(
        environment.urlApiUsuario + 'Empresa/regimeTributario',
        empresaRegimeTributarioRequest,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  public listarMultiempresa(
    textoBusca: string,
    paginaAtual: number
  ): Observable<ListaEmpresaMultiempresaResponse> {
    let url = environment.urlApiUsuario + `Empresa/multiempresa?paginaAtual=${paginaAtual}`;
    if (textoBusca) {
      url += `&textoBusca=${encodeURIComponent(textoBusca)}`;
    }

    return this.httpClient
      .get<ListaEmpresaMultiempresaResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public obterMultiempresaPorId(idEmpresa: number): Observable<EmpresaMultiempresaResponse> {
    return this.httpClient
      .get<EmpresaMultiempresaResponse>(
        environment.urlApiUsuario + `Empresa/multiempresa/idEmpresa/${idEmpresa}`,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  public salvarMultiempresa(
    idEmpresa: number,
    empresaMultiempresaRequest: EmpresaMultiempresaRequest
  ): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(
        environment.urlApiUsuario + `Empresa/multiempresa/idEmpresa/${idEmpresa}`,
        empresaMultiempresaRequest,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Salva ou remove a logo da empresa logada (usa idEmpresa do token no backend).
   */
  public salvarLogo(request: EmpresaLogoRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(
        environment.urlApiUsuario + 'Empresa/logo',
        request,
        this.addHeaderToken()
      )
      .pipe(catchError(this.handleError));
  }
}

